import { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  AT_MENTION_SEARCH_TABS,
  AtMentionSearchTab,
  DEFAULT_AT_MENTION_SEARCH_TAB,
  PopoverProps,
} from "../../types";
import InfiniteScroll from "src/components/infiniteScroll";

import styles from "./style.module.scss";
import cx from "classnames";
import TruncateWithTooltip from "src/components/truncateWithTooltip";

const VIEWPORT_EDGE_MARGIN_PX = 8;
const DefaultProfilePicture = "/images/default_user_profile_background.png";

const AtMentionPopover = ({
  items,
  coords,
  onSelect,
  isLoading,
  onDismiss,
  getMentionAnchorRoot,
  mentionTab: controlledTab,
  onMentionTabChange,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
}: PopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [localTab, setLocalTab] = useState<AtMentionSearchTab>(DEFAULT_AT_MENTION_SEARCH_TAB);
  const activeTab: AtMentionSearchTab =
    controlledTab !== undefined ? controlledTab : localTab;

  const setActiveTab = (tab: AtMentionSearchTab) => {
    if (controlledTab === undefined) {
      setLocalTab(tab);
    }
    onMentionTabChange?.(tab);
  };

  const [popoverPosition, setPopoverPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  } | null>(null);

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!coords) {
      setPopoverPosition(null);
      return;
    }

    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - (coords.top + coords.height);
    const estimatedPopoverHeight = 200;

    if (spaceBelow < estimatedPopoverHeight && coords.top > estimatedPopoverHeight) {
      setPopoverPosition({
        bottom: viewportHeight - coords.top,
        left: coords.left,
      });
    } else {
      setPopoverPosition({
        top: coords.top + coords.height,
        left: coords.left,
      });
    }
  }, [coords, items.length]);

  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el || !popoverPosition) return;

    const { width } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const margin = VIEWPORT_EDGE_MARGIN_PX;

    let left = popoverPosition.left;
    left = Math.min(left, vw - margin - width);
    left = Math.max(margin, left);

    if (left !== popoverPosition.left) {
      setPopoverPosition((prev) => (prev ? { ...prev, left } : null));
    }
  }, [popoverPosition]);

  useEffect(() => {
    if (!popoverPosition) {
      onMentionTabChange?.("assets");
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const root = popoverRef.current;
      const target = event.target;
      if (!root || !(target instanceof Node)) return;
      if (root.contains(target)) return;
      const anchor = getMentionAnchorRoot?.();
      if (anchor?.contains(target)) return;

      setPopoverPosition(null);
      onMentionTabChange?.("assets");
      onDismiss?.();
    };

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 150);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("scroll", handleScroll, true);
      clearTimeout(scrollTimerRef.current);
    };
  }, [popoverPosition, getMentionAnchorRoot, onDismiss]);

  if (!popoverPosition) return null;
  
  return (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{
        ...(popoverPosition.top !== undefined
          ? { top: popoverPosition.top + 4 }
          : { bottom: (popoverPosition.bottom ?? 0) + 4 }),
        left: popoverPosition.left,
      }}
    >
      <div className={styles.tabs}>
        {AT_MENTION_SEARCH_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setActiveTab(tab.value);
            }}
            className={cx(styles.tabButton, {
              [styles.active]: activeTab === tab.value,
            })}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className={styles.emptyState}>
          Loading...
        </div>
      ) : (
        <InfiniteScroll
          className={styles.list}
          loading={isLoadingMore}
          hasMore={hasMore}
          loadMore={() => onLoadMore?.()}
          loader={() => (
            <div className={styles.emptyState}>
              Loading...
            </div>
          )}
        >
          {items.length === 0 ? (
            <div className={styles.emptyState}>
              No {activeTab} found
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className={styles.item}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(item);
                }}
              >
              { ["assets", "people"].includes(item.mentionType ?? "") &&  <div
                  className={styles.avatar}
                >
                  <img className={cx(styles.thumbnail,{
                    [styles.asset] : item.mentionType === "assets",
                    [styles.people] : item.mentionType === "people" 
                  })} src={item.imgSrc || DefaultProfilePicture} alt="label"/>
                </div>}
                <TruncateWithTooltip text={item.label} placement="top" className={styles.label} show={isScrolling ? false : undefined} />
              </div>
            ))
          )}
        </InfiniteScroll>)}
    </div>
  );
}

export default AtMentionPopover;
